import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Reveal, Eyebrow } from "./Reveal";

const inputClass =
  "mt-1.5 w-full rounded-xl border border-line bg-paper px-4 py-3 text-sm font-medium outline-none transition-[border-color,box-shadow] focus:border-brand focus:ring-2 focus:ring-brand/20";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="text-xs font-semibold uppercase tracking-wide text-ink/50">
      {label}
    </label>
    {children}
  </div>
);

export const Register = () => {
  const [form, setForm] = useState({ name: "", address: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [donePhone, setDonePhone] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/register", form);
      setDonePhone(form.phone);
      toast.success("Booking received — we'll call you back to confirm.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const reset = () => {
    setForm({ name: "", address: "", email: "", phone: "" });
    setDonePhone(null);
  };

  return (
    <section id="register" className="relative py-24 lg:py-32" data-testid="register-section">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "linear-gradient(180deg, rgba(204,251,241,0.35) 0%, rgba(250,253,251,0) 60%)" }}
      />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-xl rounded-3xl border border-line bg-white p-8 shadow-soft sm:p-10">
            {donePhone ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="text-center"
                data-testid="register-success-panel"
              >
                <CheckCircle2 className="mx-auto h-12 w-12 text-brand" />
                <h2 className="mt-4 font-display text-3xl font-medium tracking-tight">
                  Booking received
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-ink/60">
                  Thank you! Our team will call you back at{" "}
                  <span className="font-semibold text-ink">{donePhone}</span> to confirm your
                  visit. Your first-visit discount will be applied.
                </p>
                <button
                  onClick={reset}
                  data-testid="register-again-button"
                  className="mt-6 inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-semibold text-ink transition-[transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-brand"
                >
                  Book for someone else
                </button>
              </motion.div>
            ) : (
              <>
                <Eyebrow>Book now</Eyebrow>
                <h2 className="mt-3 font-display text-3xl font-medium tracking-tight sm:text-4xl">
                  Book an <em className="italic text-brand">appointment</em>
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-ink/60">
                  Fill in the application below to receive an email with available clinics and
                  appointments in your area.
                </p>
                <form onSubmit={submit} className="mt-7 space-y-4">
                  <Field label="Full name" htmlFor="reg-name">
                    <input
                      id="reg-name"
                      required
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Jane Doe"
                      data-testid="register-input-name"
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Address" htmlFor="reg-address">
                    <input
                      id="reg-address"
                      required
                      value={form.address}
                      onChange={set("address")}
                      placeholder="City, state"
                      data-testid="register-input-address"
                      className={inputClass}
                    />
                  </Field>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Email" htmlFor="reg-email">
                      <input
                        id="reg-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={set("email")}
                        placeholder="jane@email.com"
                        data-testid="register-input-email"
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Phone number" htmlFor="reg-phone">
                      <input
                        id="reg-phone"
                        type="tel"
                        required
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="(555) 000-0000"
                        data-testid="register-input-phone"
                        className={inputClass}
                      />
                    </Field>
                  </div>
                  {error && (
                    <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="register-error">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="register-submit-button"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-3.5 text-sm font-semibold text-paper transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-brand-deep disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Send booking request
                  </button>
                </form>
                <p className="mt-5 text-center text-xs text-ink/45">
                  Questions? Write to us at{" "}
                  <a
                    href="mailto:app@assisdent.info"
                    data-testid="register-support-link"
                    className="font-semibold text-brand-deep underline decoration-brand/40 underline-offset-2 transition-colors hover:text-brand"
                  >
                    app@assisdent.info
                  </a>
                </p>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
